import { useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { Check, X, ChevronDown, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useOffer } from "@/lib/useOffer";

const fmtTime = (s) => {
  if (s === null || s === undefined) return "--:--";
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

export default function Risultati() {
  const location = useLocation();
  const { user } = useAuth();
  const { offer } = useOffer();
  const result = location.state?.result;
  const [showAll, setShowAll] = useState(false);
  const [openId, setOpenId] = useState(null);

  if (!result) return <Navigate to="/simulazioni" replace />;

  const isSample = result.mode === "sample";
  const good = isSample || result.esito === "IDONEO" || result.esito === "SUPERATO";
  const wrong = result.review.filter((r) => !r.isCorrect);
  const shown = showAll ? result.review : wrong;
  const failedBlocks = result.blockScores.filter((b) => b.passed === false);
  const isFull = result.mode === "full" || result.mode === "simulation";
  const weakest = result.weakestBlock;
  const showConversion = isSample && user && !user.hasFullAccess;

  return (
    <div className="pt-6">
      {/* Verdict */}
      <section className={`rounded-3xl p-7 text-center fade-up ${good ? "bg-[color:var(--ink)] text-white" : "bg-[color:var(--ink)] text-white"}`}>
        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/50">{isSample ? "Assaggio" : "Esito"}</p>
        <p
          className={`mt-2 ${isSample ? "text-3xl sm:text-4xl" : "text-4xl sm:text-5xl"} font-extrabold tracking-tight ${good ? "text-[color:var(--volt)]" : "text-[#ff7a7f]"}`}
          data-testid="results-verdict"
        >
          {isSample ? "Assaggio completato" : result.esito}
        </p>
        {isSample && (
          <p className="mt-2 text-sm text-white/60 leading-5 max-w-sm mx-auto">
            Nessuna soglia di idoneità: questo era un assaggio del vero test.
          </p>
        )}
        <div className="mt-5 flex items-center justify-center gap-8">
          <div>
            <p className="font-mono text-2xl font-semibold" data-testid="results-total-score">
              {result.totalCorrect}/{result.totalQuestions}
            </p>
            <p className="font-mono text-[10px] tracking-widest uppercase text-white/50 mt-1">Punteggio</p>
          </div>
          <div className="w-px h-10 bg-white/15" />
          <div>
            <p className="font-mono text-2xl font-semibold" data-testid="results-time-taken">{fmtTime(result.timeTakenSeconds)}</p>
            <p className="font-mono text-[10px] tracking-widest uppercase text-white/50 mt-1">Tempo</p>
          </div>
        </div>
        {isFull && !good && (
          <p className="mt-5 text-sm text-white/60 leading-5 max-w-sm mx-auto">
            Serve almeno 9/15 in ogni blocco: il punteggio totale non basta.
          </p>
        )}
      </section>

      {/* Per-block scores */}
      {result.blockScores.length > 0 && (
        <section className="mt-6 fade-up">
          <h2 className="text-sm font-extrabold tracking-[0.14em] uppercase mb-1">Punteggio per blocco</h2>
          {!isSample && <p className="text-xs text-[color:var(--text-2)] mb-4 font-mono">soglia {result.threshold}/15</p>}
          {isSample && <p className="text-xs text-[color:var(--text-2)] mb-4 font-mono">5 domande per blocco · nel test vero servono 9/15 in ognuno</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {result.blockScores.map((b) => {
              const isWeakest = isSample && weakest && b.blocco === weakest.blocco;
              return (
              <div
                key={b.blocco}
                data-testid="results-block-score"
                className={`rounded-3xl border p-5 ${
                  isWeakest
                    ? "border-[color:var(--ink)] bg-white"
                    : b.passed === false
                    ? "border-[color:var(--danger)]/40 bg-white"
                    : "border-[color:var(--hairline)] bg-white"
                }`}
              >
                <div className="flex items-center justify-between">
                  <p className="font-mono text-[10px] tracking-widest uppercase text-[color:var(--text-2)]">Blocco {b.blocco}</p>
                  {isSample ? (
                    isWeakest && (
                      <span className="text-[10px] font-extrabold uppercase tracking-wide rounded-full bg-[color:var(--ink)] text-white px-2 py-1" data-testid="results-weakest-badge">
                        Punto debole
                      </span>
                    )
                  ) : b.passed ? (
                    <span className="text-[10px] font-extrabold uppercase tracking-wide rounded-full bg-[color:var(--volt)] px-2 py-1">Superato</span>
                  ) : (
                    <span className="text-[10px] font-extrabold uppercase tracking-wide rounded-full border border-[color:var(--danger)]/40 text-[color:var(--danger)] px-2 py-1" data-testid="results-block-to-recover">
                      Da recuperare
                    </span>
                  )}
                </div>
                <p className="mt-2 font-bold text-sm">{b.nome}</p>
                <p className="mt-2 font-mono text-3xl font-semibold tracking-tight">
                  {b.correct}/{b.total}
                </p>
                <div
                  className="mt-3 h-1 rounded-full bg-[color:var(--bg-subtle)] overflow-hidden"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={b.total}
                  aria-valuenow={b.correct}
                  aria-label={`Blocco ${b.blocco}: ${b.correct} corrette su ${b.total}`}
                >
                  <div
                    className="h-full rounded-full transition-[width] duration-700 ease-out"
                    style={{ width: `${(b.correct / b.total) * 100}%`, background: b.passed === false ? "var(--danger)" : "var(--volt)" }}
                  />
                </div>
              </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Sample conversion block — hooked to the weakest block */}
      {showConversion && (
        <section className="mt-5 rounded-3xl bg-[color:var(--ink)] text-white p-6 fade-up" data-testid="results-sample-conversion">
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/50">Il tuo prossimo passo</p>
          <p className="mt-2 text-lg font-extrabold tracking-tight leading-6">
            {weakest ? (
              <>Il tuo punto debole è <span className="text-[color:var(--volt)]">{weakest.nome}</span>.</>
            ) : (
              <>Hai completato l'assaggio.</>
            )}{" "}
            Sbloccalo tutto: 5 simulazioni complete, tutte le lezioni e il ripasso mirato degli errori.
          </p>
          <Link
            to={offer?.attiva ? "/offerta" : "/sblocca"}
            data-testid="results-sample-unlock-button"
            className="press mt-4 inline-flex items-center gap-2 rounded-full bg-[color:var(--volt)] text-[color:var(--ink)] font-extrabold px-6 py-3 min-h-[44px] hover:brightness-95 transition-all duration-200"
          >
            <Lock className="w-4 h-4" aria-hidden="true" />
            {offer?.attiva ? "Sblocca tutto — 29 € (anziché 39 €)" : "Sblocca tutto — 39 €"}
          </Link>
          <p className="mt-2.5 text-[11px] text-white/40">Pagamento unico, accesso per 12 mesi. Nessun abbonamento.</p>
        </section>
      )}

      {/* Weakest block CTA */}
      {failedBlocks.length > 0 && (
        <section className="mt-5 rounded-3xl bg-[color:var(--bg-subtle)] border border-[color:var(--hairline)] p-5 fade-up" data-testid="results-recover-callout">
          <p className="font-bold text-sm">
            Ti manca {failedBlocks.length === 1 ? `solo ${failedBlocks[0].nome}` : `il recupero di ${failedBlocks.length} blocchi`}.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {failedBlocks.map((b) => (
              <Link
                key={b.blocco}
                to={`/esame?mode=block&blocco=${b.blocco}`}
                data-testid={`results-train-block-${b.blocco}-button`}
                className="rounded-full bg-[color:var(--ink)] text-white text-xs font-extrabold px-4 py-2.5 hover:bg-black/85 transition-colors duration-200"
              >
                Allena Blocco {b.blocco}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Review */}
      <section className="mt-8 fade-up" data-testid="results-review-wrong-answers">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-extrabold tracking-[0.14em] uppercase">
            {showAll ? "Tutte le domande" : "Errori da rivedere"}{" "}
            <span className="font-mono text-[color:var(--text-2)]">({shown.length})</span>
          </h2>
          <button
            onClick={() => setShowAll(!showAll)}
            data-testid="results-toggle-review-button"
            className="text-xs font-bold underline underline-offset-2"
          >
            {showAll ? "Solo errori" : "Mostra tutte"}
          </button>
        </div>

        {shown.length === 0 && (
          <div className="rounded-3xl border border-[color:var(--hairline)] bg-white p-6 text-center">
            <p className="font-extrabold">Zero errori. Perfetto.</p>
          </div>
        )}

        <div className="space-y-2.5">
          {shown.map((r, i) => {
            const open = openId === r.id;
            return (
              <div key={r.id} className="rounded-2xl border border-[color:var(--hairline)] bg-white overflow-hidden">
                <button
                  onClick={() => setOpenId(open ? null : r.id)}
                  data-testid="results-review-item"
                  aria-expanded={open}
                  className="w-full text-left px-4 py-3.5 flex items-start gap-3 hover:bg-[color:var(--bg-subtle)] transition-colors duration-200"
                >
                  <span className={`mt-0.5 shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${r.isCorrect ? "bg-[color:var(--volt)]" : "bg-[color:var(--danger)] text-white"}`}>
                    {r.isCorrect ? <Check className="w-3 h-3" strokeWidth={3} /> : <X className="w-3 h-3" strokeWidth={3} />}
                  </span>
                  <span className="flex-1 text-sm font-semibold leading-5">{r.testo}</span>
                  <span className="shrink-0 flex items-center gap-2">
                    <span className="font-mono text-[10px] text-[color:var(--text-2)] uppercase">B{r.blocco}</span>
                    <ChevronDown className={`w-4 h-4 text-[color:var(--text-2)] transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
                  </span>
                </button>
                {open && (
                  <div className="px-4 pb-4 pt-1 border-t border-[color:var(--hairline)] fade-up">
                    <div className="space-y-1.5 mt-3">
                      {r.opzioni.map((opt, j) => {
                        const isCorrect = j === r.correttaIndex;
                        const isSel = j === r.selectedIndex;
                        return (
                          <div
                            key={j}
                            className={`rounded-xl px-3 py-2.5 text-sm flex items-center justify-between gap-2 ${
                              isCorrect
                                ? "bg-[color:var(--volt)] font-bold"
                                : isSel
                                ? "border border-[color:var(--danger)]/50 text-[color:var(--danger)]"
                                : "bg-[color:var(--bg-subtle)] text-[color:var(--text-2)]"
                            }`}
                          >
                            <span>{opt}</span>
                            {isCorrect && <Check className="w-3.5 h-3.5 shrink-0" strokeWidth={3} />}
                            {isSel && !isCorrect && <span className="font-mono text-[10px] uppercase shrink-0">tua risposta</span>}
                          </div>
                        );
                      })}
                      {r.selectedIndex === null || r.selectedIndex === undefined ? (
                        <p className="font-mono text-[11px] text-[color:var(--text-2)] uppercase pt-1">Non hai risposto</p>
                      ) : null}
                    </div>
                    <div className="mt-3 rounded-xl bg-[color:var(--bg-subtle)] p-3">
                      <p className="font-mono text-[10px] tracking-widest uppercase text-[color:var(--text-2)] mb-1">Spiegazione</p>
                      <p className="text-sm leading-6">{r.spiegazione}</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Actions */}
      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        {showConversion ? (
          <Link
            to={offer?.attiva ? "/offerta" : "/sblocca"}
            data-testid="results-new-simulation-button"
            className="press flex-1 text-center rounded-full bg-[color:var(--ink)] text-white font-extrabold px-5 py-3.5 min-h-[44px] hover:bg-black/85 transition-colors duration-200"
          >
            {offer?.attiva ? "Sblocca a 29 €" : "Sblocca l'accesso completo"}
          </Link>
        ) : (
          <Link
            to="/simulazioni"
            data-testid="results-new-simulation-button"
            className="press flex-1 text-center rounded-full bg-[color:var(--ink)] text-white font-extrabold px-5 py-3.5 min-h-[44px] hover:bg-black/85 transition-colors duration-200"
          >
            Nuova simulazione
          </Link>
        )}
        <Link
          to="/"
          data-testid="results-back-home-button"
          className="press flex-1 text-center rounded-full border border-[color:var(--hairline)] bg-white font-semibold px-5 py-3.5 min-h-[44px] hover:bg-[color:var(--bg-subtle)] transition-colors duration-200"
        >
          Torna alla home
        </Link>
      </div>
    </div>
  );
}
